/*
 * The justified rows of the image lists (concepts/frontend-design.md, "Lists"): the image list
 * of an event, the pageable list, the image list of the events, the image list of the event
 * module and of an event tag. Every row is filled with whole images in their own proportions
 * which end exactly at the right edge of the list - nothing is ever cropped -, with the options
 * of 6.x read from the data attributes of the list:
 *
 * - data-rowheight: the height a row aims for (the option "Height of a thumbnail"); on a
 *   narrow list a row aims for at most 30 % of the width (at least 5rem), the same rule as
 *   the stylesheet's first paint (imagelist.less), so a phone shows two or three images in a
 *   row; every row aims for the same height;
 * - data-firstimagerowheight: how many rows the first image spans ("Height of the first
 *   thumbnail"); the rows beside it are justified to the rest of the width, and the first
 *   image is exactly as high as they are with the gaps between them;
 * - data-dofilllastrow: whether the last row fills the width like the others (the modules,
 *   the content plugin) or keeps the height it aimed for.
 *
 * The script writes the box of every item as custom properties (EventgalleryImage.js),
 * which imagelist.less turns into its size; the list stays a wrapping flex row whose lines
 * are the rows (the rows beside the first image start after it by a margin, the first image
 * reaches down past its first line by a negative margin). It lays the list out as soon as it
 * is bound and again whenever the width of the list changes (a ResizeObserver: a window, a
 * sidebar, a tab or an accordion which shows the list), and when a file turns out to have
 * other proportions than the page said. Until 7.0.0 the script ran half a second after a
 * window resize, set pixel sizes on the images and cropped the first image and the rows
 * beside it to a fixed height.
 */
import Image from "./EventgalleryImage";
import Row from "./EventgalleryRow";

/** a row on a narrow list aims for at most this share of the width */
const NARROW_ROW = 0.3;
/** a row never aims for less than this, in rem */
const MIN_ROW_REM = 5;
/** the rows end this much before the edge, so the browser never wraps an item by rounding */
const SAFETY = 0.25;

const instances = new WeakMap();

export default class Imagelist {

    /**
     * @param {HTMLElement} container the list (.eventgallery-imagelist)
     * @param {object} [options] values which win over the data attributes of the list
     */
    constructor(container, options = {}) {
        this.options = Object.assign({
            rowHeight: 250,
            firstImageRowHeight: 1,
            minImageWidth: 150,
            doFillLastRow: false,
            layoutComplete: () => {},
        }, container ? Imagelist.readOptions(container) : {}, options);
        this.container = container;
        this.images = [];
        this.width = 0;
        this.frame = 0;
        this.collect();
    }

    /**
     * Lays out every image list below root: a new one at once, one the browser is still
     * parsing (whileParsing) with the images it has so far - the rows before the last one
     * stay what they are when more images arrive.
     *
     * @param {Document|HTMLElement} root
     * @returns {Imagelist[]} the lists bound now for the first time
     */
    static bind(root = document) {
        const bound = [];
        root.querySelectorAll('.eventgallery-imagelist').forEach((container) => {
            const known = instances.get(container);
            if (known) {
                if (known.collect()) {
                    known.layout();
                }
                return;
            }
            const list = new Imagelist(container);
            instances.set(container, list);
            list.start();
            bound.push(list);
        });
        return bound;
    }

    /**
     * Takes the items of the list which hold an image; an item the browser has already
     * collected keeps what it learned (the proportions of its loaded file).
     *
     * @returns {boolean} whether the items changed
     */
    collect() {
        if (!this.container) {
            return false;
        }
        const elements = Array.from(this.container.children).filter((element) => element.querySelector('img'));
        if (elements.length === this.images.length && elements.every((element, index) => this.images[index].element === element)) {
            return false;
        }
        const known = new Map(this.images.map((image) => [image.element, image]));
        this.images = elements.map((element) => {
            const image = known.get(element) || new Image(element);
            image.takeNaturalRatio();
            return image;
        });
        return true;
    }

    /**
     * @param {HTMLElement} container
     * @returns {object} the options the data attributes of the list give
     */
    static readOptions(container) {
        const options = {};
        const number = (name, key) => {
            const value = parseInt(container.getAttribute(name), 10);
            if (!Number.isNaN(value)) {
                options[key] = value;
            }
        };
        number('data-rowheight', 'rowHeight');
        number('data-firstimagerowheight', 'firstImageRowHeight');
        if (container.hasAttribute('data-dofilllastrow')) {
            options.doFillLastRow = container.getAttribute('data-dofilllastrow') === 'true';
        }
        return options;
    }

    start() {
        this.layout();
        this.observe();
        this.watchImages();
    }

    /**
     * @returns {number} the width of the content box of the list in CSS pixels, with fractions
     */
    measure() {
        const style = window.getComputedStyle(this.container);
        const px = (value) => parseFloat(value) || 0;
        return this.container.getBoundingClientRect().width
            - px(style.paddingLeft) - px(style.paddingRight)
            - px(style.borderLeftWidth) - px(style.borderRightWidth);
    }

    /**
     * Lays the list out for its width; a list without a width (in a hidden tab) waits until
     * it gets one.
     *
     * @param {number} [width] the width of the content box, measured by default
     */
    layout(width = this.measure()) {
        if (!(width > 0) || this.images.length === 0) {
            return;
        }
        // the gaps and the root font size, read once for a width: a list laid out again while
        // the page is parsed asks for nothing else (a box which did not change is not written
        // again either, Image.setSize())
        if (width !== this.width || !this.spacing) {
            const style = window.getComputedStyle(this.container);
            const rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
            this.spacing = {
                gap: parseFloat(style.columnGap) || 0,
                rowGap: parseFloat(style.rowGap) || 0,
                minRowHeight: MIN_ROW_REM * rem,
            };
        }
        this.width = width;
        const boxes = Imagelist.compute(this.images.map((image) => image.ratio), Object.assign({}, this.options, { width }, this.spacing));
        boxes.forEach((box, index) => this.images[index].setSize(box));
        this.container.classList.add('eg-rows--laid-out');
        this.options.layoutComplete(this);
    }

    /**
     * The boxes of the images of a list: pure arithmetic, nothing is read from the page. The
     * server repeats it for the estimate of the first row (ThumbnailOptions::rowWidths()): a
     * change of the rule here or in EventgalleryRow.js is a change there.
     *
     * @param {number[]} ratios the proportions of the images, width divided by height
     * @param {object} options rowHeight, firstImageRowHeight, minImageWidth,
     *        doFillLastRow, and the width of the list, the gap between two images (gap) and
     *        between two rows (rowGap), the lowest height a row aims for (minRowHeight)
     * @returns {{width: number, height: number, indent?: number, overhang?: number, row: number}[]}
     *          one box per image, in CSS pixels, with the number of its row (the first image
     *          spanning several rows is in the first row)
     */
    static compute(ratios, options) {
        const o = Object.assign({ gap: 0, rowGap: 0, minRowHeight: 0, minImageWidth: 150, firstImageRowHeight: 1, doFillLastRow: false }, options);
        const width = o.width - SAFETY;
        const base = o.rowHeight > 0 ? o.rowHeight : 250;
        const target = Math.min(base, Math.max(o.minRowHeight, o.width * NARROW_ROW));
        const boxes = new Array(ratios.length);
        let queue = ratios.map((ratio, index) => ({ ratio, index }));

        let rowNumber = 0;
        if (o.firstImageRowHeight > 1 && queue.length > 1) {
            const block = Imagelist.firstImageBlock(queue, boxes, Object.assign({}, o, { width, target }));
            queue = queue.slice(block.images);
            rowNumber = block.rows;
        }

        while (queue.length > 0) {
            const row = new Row({ maxWidth: width, maxHeight: target, gap: o.gap, joinsLoneImage: true });
            while (queue.length > 0 && row.add(queue[0])) {
                queue.shift();
            }
            const keep = queue.length === 0 && !o.doFillLastRow && row.fitsAtItsHeight(width);
            Imagelist.place(row, keep ? target : row.justifiedHeight(width), boxes, 0, rowNumber++);
        }
        return boxes;
    }

    /**
     * The first image over several rows and the rows beside it. The rows beside it are filled
     * at the height of a row and then justified to the width left of the first image; the
     * height of the first image follows from them - it is the sum of their heights and the
     * gaps between them, and its width is its proportions times that height, which is the
     * width the rows beside it are justified to. One linear equation gives both. When the first
     * image would leave less than the minimum image width beside it, or the rows beside it
     * would make it far higher or lower than the rows it should span, it stands in the rows
     * like the others (6.x cropped it to the full width instead).
     *
     * @returns {{images: number, rows: number}} how many images and rows the block took,
     *          none without a block
     */
    static firstImageBlock(queue, boxes, o) {
        const none = { images: 0, rows: 0 };
        const first = queue[0];
        const rows = o.firstImageRowHeight;
        const aimedHeight = rows * o.target + (rows - 1) * o.rowGap;
        const aimedWidth = first.ratio * aimedHeight;
        if (aimedWidth + o.gap + o.minImageWidth >= o.width) {
            return none;
        }

        const sideWidth = o.width - o.gap - aimedWidth;
        const sideRows = [];
        let next = 1;
        while (next < queue.length && sideRows.length < rows) {
            const row = new Row({ maxWidth: sideWidth, maxHeight: o.target, gap: o.gap });
            while (next < queue.length && row.add(queue[next])) {
                next++;
            }
            sideRows.push(row);
        }
        const lastRow = next >= queue.length && !o.doFillLastRow ? sideRows[sideRows.length - 1] : null;

        // height H of the first image: H = sum of the rows + gaps, a justified row j is
        // (S - (n_j - 1) gap) / R_j high with S = width - gap - ratio * H the width beside it
        const solve = (keepLast) => {
            let a = 0;
            let c = (sideRows.length - 1) * o.rowGap;
            sideRows.forEach((row) => {
                if (keepLast && row === lastRow) {
                    c += o.target;
                } else {
                    a += 1 / row.ratioSum();
                    c -= (row.images.length - 1) * o.gap / row.ratioSum();
                }
            });
            const height = (a * (o.width - o.gap) + c) / (1 + a * first.ratio);
            return { height, side: o.width - o.gap - first.ratio * height };
        };
        let solution = solve(lastRow !== null);
        let keepLast = lastRow !== null;
        if (keepLast && !lastRow.fitsAtItsHeight(solution.side)) {
            keepLast = false;
            solution = solve(false);
        }
        const { height, side } = solution;
        if (!(height > 0) || side < o.minImageWidth || height > aimedHeight * 2 || height < aimedHeight / 2) {
            return none;
        }

        const firstWidth = Imagelist.floor(first.ratio * height);
        const heights = sideRows.map((row) => (keepLast && row === lastRow) ? o.target : row.justifiedHeight(side));
        sideRows.forEach((row, index) => {
            Imagelist.place(row, heights[index], boxes, index === 0 ? 0 : firstWidth + o.gap, index);
        });
        boxes[first.index] = { width: firstWidth, height, overhang: heights[0] - height, row: 0 };
        return { images: next, rows: sideRows.length };
    }

    /**
     * The boxes of the images of a row at a height.
     *
     * @param {Row} row
     * @param {number} height
     * @param {object[]} boxes
     * @param {number} indent the space before the first image of the row
     * @param {number} number the number of the row
     */
    static place(row, height, boxes, indent, number) {
        row.images.forEach((image, index) => {
            const box = { width: Imagelist.floor(image.ratio * height), height, row: number };
            if (index === 0 && indent > 0) {
                box.indent = indent;
            }
            boxes[image.index] = box;
        });
    }

    /**
     * @param {number} value
     * @returns {number} the value down to the 1/64 px a browser lays out with, so the
     *          widths of a row never add up to more than the row
     */
    static floor(value) {
        return Math.floor(value * 64) / 64;
    }


    /**
     * Lays the list out again when its width changes. The new layout waits for the next frame:
     * it changes the height of the list, which the observer would otherwise report within the
     * same frame as a loop.
     */
    observe() {
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', () => this.schedule());
            return;
        }
        this.observer = new ResizeObserver((entries) => {
            const width = entries[entries.length - 1].contentRect.width;
            if (width !== this.width) {
                this.schedule();
            }
        });
        this.observer.observe(this.container);
    }

    /**
     * A file whose proportions differ from the ones the page said lays the list out again; a
     * file which is too narrow for its box is replaced by a wider one (Image.fitFile()).
     */
    watchImages() {
        this.container.addEventListener('load', (event) => {
            const image = this.images.find((candidate) => candidate.img === event.target);
            if (image && image.takeNaturalRatio()) {
                this.schedule();
            } else if (image) {
                image.fitFile();
            }
        }, true);
    }

    schedule() {
        if (this.frame) {
            return;
        }
        this.frame = window.requestAnimationFrame(() => {
            this.frame = 0;
            this.layout();
        });
    }
}
